// Groups page (Build P2d) — manage group homes + group shifts (SIL / day-program / community group).
// Brevity §12 "By Group" surface, in Rostery design. Group-shift money is per-participant ratio
// billing handled by the backend money engine; this page is the coordinator workspace.
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Home, Plus, Pencil, Trash2, X, Users, Calendar, ChevronLeft, ChevronRight, Loader2, Grid3x3, ArrowRight, DollarSign, Building2 } from 'lucide-react';
import { format, startOfWeek, addDays, addWeeks, subWeeks } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import { useConfirm } from '../../components/ConfirmDialog';
import GroupShiftModal from './GroupShiftModal';
import { fmtTime12 } from '../../utils/time';

const money = (n) => n == null ? '$0.00' : `$${Number(n).toFixed(2)}`;

const container = { hidden: {}, show: { transition: { staggerChildren: .06, delayChildren: .04 } } };
const fadeUp    = { hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: .28 } } };

function StatCard({ icon: Icon, label, value, sub, gradient }) {
  return (
    <motion.div variants={fadeUp} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03]" style={{ background: gradient }} />
      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 shadow-sm" style={{ background: gradient }}>
        <Icon size={18} className="text-white" />
      </div>
      <p className="text-2xl font-bold text-slate-900 leading-none">{value}</p>
      <p className="text-sm font-medium text-slate-500 mt-1">{label}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </motion.div>
  );
}

export default function Groups() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [homes, setHomes] = useState([]);
  const [clients, setClients] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [silTemplates, setSilTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [homeModal, setHomeModal] = useState(null);     // {editing?} | null
  const [shiftModal, setShiftModal] = useState(null);   // {shiftId?} | null
  const [silModal, setSilModal] = useState(false);

  const weekEnd = addDays(weekStart, 6);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, sh, cl, sil] = await Promise.all([
        api.get('/groups/group-homes'),
        api.get(`/groups/group-shifts?start_date=${format(weekStart, 'yyyy-MM-dd')}&end_date=${format(weekEnd, 'yyyy-MM-dd')}`),
        api.get('/clients'),
        api.get('/sil/templates').catch(() => ({ data: [] })),
      ]);
      setHomes(h.data || []);
      setShifts(sh.data || []);
      // SIL/group scheduling is SERVICE DELIVERY — Plan-Management / Support-Coordination
      // participants (finance-only, service_type set) never belong in these pickers.
      const allClients = Array.isArray(cl.data) ? cl.data : cl.data.clients || [];
      setClients(allClients.filter((c) => !['plan_managed', 'support_coordination'].includes(c.service_type)));
      setSilTemplates(sil.data || []);
    } catch { toast.error('Failed to load groups'); }
    finally { setLoading(false); }
  }, [weekStart]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const delHome = async (h) => {
    if (!await confirm(`Archive "${h.name}"?`, { title: 'Archive Group Home', danger: true })) return;
    try { await api.delete(`/groups/group-homes/${h.id}`); load(); } catch { toast.error('Failed'); }
  };

  const totalResidents = homes.reduce((s, h) => s + (Number(h.resident_count) || 0), 0);
  const weeklyCharge   = shifts.reduce((s, x) => s + (Number(x.total_client_charge) || 0), 0);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary-500" /></div>;

  return (
    <motion.div variants={container} initial="hidden" animate="show" className="p-6 max-w-6xl mx-auto space-y-6">

      {/* Header */}
      <motion.div variants={fadeUp} className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Group Services</h1>
          <p className="text-sm text-slate-500 mt-0.5">Group homes (SIL / day programs) and group shifts with per-participant ratio billing.</p>
        </div>
        <button onClick={() => setShiftModal({})} className="btn-primary btn-sm gap-1.5"><Plus size={15} /> New Group Shift</button>
      </motion.div>

      {/* Stat cards */}
      <motion.div variants={container} className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Home}       label="Group Homes"   value={homes.length}        sub="SIL / day programs"        gradient="linear-gradient(135deg,#6366f1,#4f46e5)" />
        <StatCard icon={Users}      label="Residents"     value={totalResidents}      sub="across all homes"          gradient="linear-gradient(135deg,#0ea5e9,#0284c7)" />
        <StatCard icon={Calendar}   label="Shifts"        value={shifts.length}       sub="this week"                 gradient="linear-gradient(135deg,#10b981,#059669)" />
        <StatCard icon={DollarSign} label="Weekly Charge" value={money(weeklyCharge)} sub="participant ratio billing" gradient="linear-gradient(135deg,#f59e0b,#d97706)" />
      </motion.div>

      {/* Group homes */}
      <motion.section variants={fadeUp} className="card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <p className="font-semibold text-slate-800 text-sm flex items-center gap-2"><Building2 size={15} className="text-primary-500" /> Group Homes</p>
            <p className="text-xs text-slate-400 mt-0.5">{homes.length} home{homes.length !== 1 ? 's' : ''} · {totalResidents} resident{totalResidents !== 1 ? 's' : ''}</p>
          </div>
          <button onClick={() => setHomeModal({})} className="btn-secondary btn-sm gap-1.5"><Plus size={13} /> Add home</button>
        </div>
        {homes.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-14 text-slate-400">
            <Home size={34} className="opacity-20" />
            <p className="text-sm font-medium">No group homes yet</p>
            <button onClick={() => setHomeModal({})} className="btn-primary btn-sm gap-1.5"><Plus size={13} /> Add your first home</button>
          </div>
        ) : (
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {homes.map(h => (
              <div key={h.id} className="card-hover p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2.5">
                    <span className="w-9 h-9 rounded-xl flex items-center justify-center text-white shadow-sm" style={{ background: h.color || '#0891b2' }}><Home size={16} /></span>
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">{h.name}</p>
                      <p className="text-xs text-slate-400 capitalize">{(h.home_type || 'sil').replace('_', ' ')}</p>
                    </div>
                  </div>
                  <div className="flex gap-0.5">
                    <button title="SIL roster grid" onClick={() => { const t = silTemplates.find(x => x.group_home_id === h.id); t ? navigate(`/sil/templates/${t.id}`) : setSilModal(true); }} className="p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"><Grid3x3 size={14} /></button>
                    <button onClick={() => setHomeModal({ editing: h })} className="p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"><Pencil size={14} /></button>
                    <button onClick={() => delHome(h)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={14} /></button>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-xs">
                  <span className="badge-gray"><Users size={12} /> {h.resident_count} resident{h.resident_count === 1 ? '' : 's'}</span>
                  {h.suburb && <span className="text-slate-400">{h.suburb}{h.state ? `, ${h.state}` : ''}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.section>

      {/* Group shifts this week */}
      <motion.section variants={fadeUp} className="card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <p className="font-semibold text-slate-800 text-sm flex items-center gap-2"><Calendar size={15} className="text-primary-500" /> Group Shifts</p>
            <p className="text-xs text-slate-400 mt-0.5">{shifts.length} shift{shifts.length !== 1 ? 's' : ''} · {money(weeklyCharge)} charged</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setWeekStart(w => subWeeks(w, 1))} className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500"><ChevronLeft size={14} /></button>
            <span className="text-xs font-medium text-slate-600 w-40 text-center">{format(weekStart, 'd MMM')} – {format(weekEnd, 'd MMM yyyy')}</span>
            <button onClick={() => setWeekStart(w => addWeeks(w, 1))} className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500"><ChevronRight size={14} /></button>
          </div>
        </div>
        {shifts.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-14 text-slate-400">
            <Calendar size={34} className="opacity-20" />
            <p className="text-sm font-medium">No group shifts this week</p>
            <button onClick={() => setShiftModal({})} className="btn-primary btn-sm gap-1.5"><Plus size={13} /> New Group Shift</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-100">
                  <th className="px-4 py-3">Date</th><th className="px-3 py-3">Time</th>
                  <th className="px-3 py-3">Service</th><th className="px-3 py-3">Participants</th>
                  <th className="px-3 py-3">Carer</th><th className="px-3 py-3 text-center">Ratio</th>
                  <th className="px-3 py-3 text-right">Charge</th><th className="px-3 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {shifts.map((s, i) => (
                  <motion.tr key={s.id} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * .02, duration: .18 }}
                    className="hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => setShiftModal({ shiftId: s.id })}>
                    <td className="px-4 py-3 font-medium text-slate-800 whitespace-nowrap">{format(new Date(s.shift_date + 'T00:00:00'), 'EEE d MMM')}</td>
                    <td className="px-3 py-3 text-slate-600 whitespace-nowrap">{fmtTime12(s.start_time)}–{fmtTime12(s.end_time)}</td>
                    <td className="px-3 py-3 text-slate-600">{s.shift_type}</td>
                    <td className="px-3 py-3 text-slate-600"><span className="inline-flex items-center gap-1"><Users size={12} className="text-slate-400" />{s.participant_count}</span> <span className="text-xs text-slate-400">{s.participant_names || '—'}</span></td>
                    <td className="px-3 py-3 text-slate-600">{s.staff_name || <span className="text-amber-600">Unassigned</span>}</td>
                    <td className="px-3 py-3 text-center text-slate-600">1:{s.default_ratio_den || 1}</td>
                    <td className="px-3 py-3 text-right font-semibold text-slate-800">{money(s.total_client_charge)}</td>
                    <td className="px-3 py-3"><span className={`capitalize ${s.status === 'invoiced' ? 'badge-green' : s.status === 'pending' ? 'badge-yellow' : 'badge-gray'}`}>{s.status}</span></td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.section>

      {/* SIL roster templates */}
      <motion.section variants={fadeUp} className="card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <p className="font-semibold text-slate-800 text-sm flex items-center gap-2"><Grid3x3 size={15} className="text-primary-500" /> SIL Roster Templates</p>
            <p className="text-xs text-slate-400 mt-0.5">Weekly ratio + staff roster grids for a home</p>
          </div>
          <button onClick={() => setSilModal(true)} className="btn-secondary btn-sm gap-1.5"><Plus size={13} /> New template</button>
        </div>
        {silTemplates.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-14 text-slate-400">
            <Grid3x3 size={34} className="opacity-20" />
            <p className="text-sm font-medium">No SIL templates yet</p>
            <p className="text-xs text-slate-400 max-w-xs text-center -mt-1.5">Create one to build a weekly ratio + staff roster grid for a home.</p>
            <button onClick={() => setSilModal(true)} className="btn-primary btn-sm gap-1.5"><Plus size={13} /> New template</button>
          </div>
        ) : (
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {silTemplates.map(t => (
              <button key={t.id} onClick={() => navigate(`/sil/templates/${t.id}`)} className="card-hover text-left p-4 group">
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-slate-800 text-sm">{t.name}</p>
                  <ArrowRight size={14} className="text-slate-300 group-hover:text-primary-500 group-hover:translate-x-0.5 transition-all" />
                </div>
                <p className="text-xs text-slate-400 mt-1">{t.group_home_name || 'No home'} · {t.participant_count} participants · {t.template_type}{t.is_locked ? ' · 🔒' : ''}</p>
              </button>
            ))}
          </div>
        )}
      </motion.section>

      {homeModal && <GroupHomeModal home={homeModal.editing} clients={clients} onClose={() => setHomeModal(null)} onSaved={() => { setHomeModal(null); load(); }} />}
      {shiftModal && <GroupShiftModal shiftId={shiftModal.shiftId} groupHomes={homes} onClose={() => setShiftModal(null)} onSaved={load} />}
      {silModal && <SilTemplateCreateModal homes={homes} clients={clients} onClose={() => setSilModal(false)} onCreated={(id) => navigate(`/sil/templates/${id}`)} />}
    </motion.div>
  );
}

// ── Group Home create/edit modal ──────────────────────────────────────────────
function GroupHomeModal({ home, clients, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: home?.name || '', homeType: home?.home_type || 'sil', address: home?.address || '',
    suburb: home?.suburb || '', state: home?.state || '', postcode: home?.postcode || '',
    color: home?.color || '#0891b2', maxResidents: home?.max_residents || '',
  });
  const [residentIds, setResidentIds] = useState([]);
  const [residentsTouched, setResidentsTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const save = async () => {
    if (!form.name.trim()) return toast.error('Name required');
    setSaving(true);
    try {
      const payload = { ...form, maxResidents: form.maxResidents ? parseInt(form.maxResidents, 10) : null };
      // On edit, only replace residents if the user actually changed them (avoids wiping the existing set).
      if (!home?.id || residentsTouched) payload.clientIds = residentIds;
      if (home?.id) await api.put(`/groups/group-homes/${home.id}`, payload);
      else await api.post('/groups/group-homes', payload);
      toast.success('Saved'); onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const toggle = (id) => { setResidentsTouched(true); setResidentIds(r => r.includes(id) ? r.filter(x => x !== id) : [...r, id]); };
  const filtered = clients.filter(c => !search || `${c.first_name} ${c.last_name}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="font-semibold text-slate-800">{home?.id ? 'Edit Group Home' : 'New Group Home'}</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400"><X size={16} /></button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <F label="Name"><input className="input-field" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></F>
            <F label="Type">
              <select className="input-field" value={form.homeType} onChange={e => setForm(f => ({ ...f, homeType: e.target.value }))}>
                <option value="sil">SIL</option><option value="group_program">Group program</option><option value="day_program">Day program</option>
              </select>
            </F>
          </div>
          <F label="Address"><AddressAutocomplete className="input-field" value={form.address} onChange={v => setForm(f => ({ ...f, address: v }))} onSelect={a => setForm(f => ({ ...f, address: [a.street, a.suburb, a.state, a.postcode].filter(Boolean).join(', ') }))} /></F>
          <div className="grid grid-cols-3 gap-3">
            <F label="Suburb"><input className="input-field" value={form.suburb} onChange={e => setForm(f => ({ ...f, suburb: e.target.value }))} /></F>
            <F label="State"><input className="input-field" value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} /></F>
            <F label="Postcode"><input className="input-field" value={form.postcode} onChange={e => setForm(f => ({ ...f, postcode: e.target.value }))} /></F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Colour"><input type="color" className="input-field h-10 p-1" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))} /></F>
            <F label="Max residents"><input type="number" min="0" className="input-field" value={form.maxResidents} onChange={e => setForm(f => ({ ...f, maxResidents: e.target.value }))} /></F>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Residents {residentIds.length > 0 && `(${residentIds.length})`}</label>
            <input className="input-field mb-2" placeholder="Search clients…" value={search} onChange={e => setSearch(e.target.value)} />
            <div className="max-h-40 overflow-y-auto space-y-1 border border-slate-100 rounded-lg p-2">
              {filtered.map(c => (
                <label key={c.id} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer">
                  <input type="checkbox" checked={residentIds.includes(c.id)} onChange={() => toggle(c.id)} className="rounded accent-primary-600" />
                  {c.first_name} {c.last_name}
                </label>
              ))}
              {filtered.length === 0 && <p className="text-xs text-slate-400 py-2 text-center">No clients</p>}
            </div>
            {home?.id && <p className="text-[11px] text-slate-400 mt-1">Re-select residents to update the list (saving replaces the current set).</p>}
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="btn-secondary btn-sm">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary btn-sm">{saving ? <Loader2 size={14} className="animate-spin" /> : null} Save</button>
        </div>
      </div>
    </div>
  );
}

function F({ label, children }) { return <div><label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>{children}</div>; }

// ── SIL template create modal ──
function SilTemplateCreateModal({ homes, clients, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', groupHomeId: '', templateType: 'group', clientId: '' });
  const [participantIds, setParticipantIds] = useState([]);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) return toast.error('Name required');
    if (form.templateType === 'individual' && !form.clientId) return toast.error('Pick the client for an individual template');
    setSaving(true);
    try {
      const { data: t } = await api.post('/sil/templates', form);
      const ids = form.templateType === 'individual' ? [form.clientId] : participantIds;
      if (ids.length) await api.post(`/sil/templates/${t.id}/clients`, { clientIds: ids }).catch(() => {});
      toast.success('Template created'); onCreated(t.id);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };
  const toggle = (id) => setParticipantIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const filtered = clients.filter(c => !search || `${c.first_name} ${c.last_name}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100"><h3 className="font-semibold text-slate-800">New SIL Roster Template</h3><button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400"><X size={16} /></button></div>
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
          <F label="Name"><input className="input-field" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. 12 Smith St — Weekly" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Group Home"><select className="input-field" value={form.groupHomeId} onChange={e => setForm(f => ({ ...f, groupHomeId: e.target.value }))}><option value="">— None —</option>{homes.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}</select></F>
            <F label="Type"><select className="input-field" value={form.templateType} onChange={e => setForm(f => ({ ...f, templateType: e.target.value }))}><option value="group">Group</option><option value="individual">Individual</option></select></F>
          </div>
          {form.templateType === 'individual' ? (
            <F label="Client"><select className="input-field" value={form.clientId} onChange={e => setForm(f => ({ ...f, clientId: e.target.value }))}><option value="">Select…</option>{clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}</select></F>
          ) : (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Participants {participantIds.length > 0 && `(${participantIds.length})`}</label>
              <input className="input-field mb-2" placeholder="Search clients…" value={search} onChange={e => setSearch(e.target.value)} />
              <div className="max-h-40 overflow-y-auto space-y-1 border border-slate-100 rounded-lg p-2">
                {filtered.map(c => <label key={c.id} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer"><input type="checkbox" checked={participantIds.includes(c.id)} onChange={() => toggle(c.id)} className="rounded accent-primary-600" />{c.first_name} {c.last_name}</label>)}
                {filtered.length === 0 && <p className="text-xs text-slate-400 py-2 text-center">No clients</p>}
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100"><button onClick={onClose} className="btn-secondary btn-sm">Cancel</button><button onClick={save} disabled={saving} className="btn-primary btn-sm">{saving ? 'Creating…' : 'Create & open grid'}</button></div>
      </div>
    </div>
  );
}
